import { useEffect, useState, useContext, useRef } from "react";
import { Container, Row, Col, Stack } from "react-bootstrap";
import { fetchPlotData } from "../../../network/plotdata";
import ScrollableContainer from "../../ScrollableContainer";
import FilterCheckbox from "../filters/FilterCheckbox";
import DropdownButton from "../DropdownButton";
import { BsFilter } from "react-icons/bs";
import GraphCard from "./GraphCard";
import { ZoomProvider, ZoomContext } from "./ZoomContext";
import { useSessionData } from "../../../sessions/useSessionData";

type GraphsContainerProps = {
  sessionId: string;
  sensorId: string;
};

// This container holds the graphs for the session detail page with a control element to show/hide different graphs
export default function GraphsContainer(props: GraphsContainerProps) {
  // Use cached session data
  const { plotData: cachedPlotData, isLoading: isCachedLoading } = useSessionData({ 
    sessionId: props.sessionId, 
    sensorId: props.sensorId 
  });
  
  const [loading, setLoading] = useState(false);
  const [selectedGraphs, setSelectedGraphs] = useState<string[]>(
    // if graph.default is true, add it to the selectedGraphs
    createGraphsArray()
      .filter((graph) => graph.default)
      .map((graph) => graph.id)
    // createGraphsArray().map((graph) => graph.id)
    // []
  );
  const [plotData, setPlotData] = useState<any>(null); // TODO: replace with actual type

  // Sync plotData with cached data or fetch plotData when sensorId/sessionId changes
  useEffect(() => {
    if (cachedPlotData && cachedPlotData.length > 0) {
      setPlotData(cachedPlotData);
    }
  }, [cachedPlotData]);

  useEffect(() => {
    let isSubscribed = true;
    if (props.sessionId && props.sensorId) {
      setLoading(true);
      fetchPlotData(props.sessionId, undefined, undefined, undefined, props.sensorId, undefined)
        .then((result) => {
          if (isSubscribed && result && result.status === 200) {
            setPlotData(result.data);
          }
        })
        .catch((err) => {
          console.error("Error fetching plot data for sensor:", err);
        })
        .finally(() => {
          if (isSubscribed) setLoading(false);
        });
    }
    return () => {
      isSubscribed = false;
    };
  }, [props.sessionId, props.sensorId]);

  function handleGraphSelectionChange(graphId: string) {
    if (selectedGraphs.includes(graphId)) {
      setSelectedGraphs(selectedGraphs.filter((id) => id !== graphId));
    } else {
      setSelectedGraphs([...selectedGraphs, graphId]);
    }
  }

  const filterControl = (
    <>
      {createGraphsArray().map((graph) => (
        <FilterCheckbox
          key={graph.id}
          controlId={graph.id}
          label={graph.name}
          checked={selectedGraphs.includes(graph.id)}
          onChange={() => handleGraphSelectionChange(graph.id)}
        />
      ))}
    </>
  );

  const filterContent = (
    <Container className="filter-drop-container py-2 px-3">
      <Row>
        <Col>
          <span className="filter-drop-heading">Filters</span>
        </Col>
        <Col className="d-flex justify-content-end">
          <a
            onClick={() =>
              setSelectedGraphs(createGraphsArray().map((graph) => graph.id))
            }
            className="wind-link"
            style={{ textDecoration: "none" }}
          >
            Select All
          </a>
        </Col>
      </Row>
      <Row className="divider my-3 mx-0" />
      <Row className="py-2">
        <Col>
          <span className="filter-drop-subheading">Graphs</span>
        </Col>
      </Row>
      <Row className="py-2">
        <Col>
          <ScrollableContainer content={filterControl} maxHeight="20vh" />
        </Col>
      </Row>
    </Container>
  );

  var content = <></>;
  if (plotData !== undefined && plotData !== null && plotData.length === 0) {
    content = (
      <Row>
        <Col className="d-flex justify-content-center">
          <h2>No data available</h2>
        </Col>
      </Row>
    );
  } else if (
    plotData !== undefined &&
    plotData !== null &&
    selectedGraphs.length === 0
  ) {
    content = (
      <Row>
        <Col className="d-flex justify-content-center">
          <h2>No graphs selected</h2>
        </Col>
      </Row>
    );
  }


  if (
    plotData !== undefined &&
    plotData !== null &&
    plotData.length > 0 &&
    selectedGraphs.length > 0
  ) {
    content = (
      <Stack gap={2}>
        {createGraphsArray()
          .filter((graph) => selectedGraphs.includes(graph.id))
          .map((graph) => (
            <GraphCard
              key={graph.id}
              fileId={props.sessionId}
              sensor_id={props.sensorId}
              title={graph.name}
              id={graph.id}
              xKey={"timestamp"}
              yKey={graph.yKey}
              plotData={plotData}
            />
          ))}
      </Stack>
    );
  }

  // Create a wrapper component that can access the ZoomContext
  const GraphsWithZoomContext = () => {
    const zoomContext = useContext(ZoomContext);
    
    // Track which timeRanges we've already processed
    const processedUpdates = useRef<Set<number>>(new Set());
    
    // Update plotData when timeRange changes due to user action
    useEffect(() => {
      // Skip if context is undefined
      if (!zoomContext) {
        return;
      }
      
      // Skip if:
      // 1. Not a user action
      // 2. No timeRange data
      // 3. We've already processed this update (using timestamp to track)
      if (!zoomContext.isUserAction || 
          !zoomContext.timeRange.start || 
          !zoomContext.timeRange.end ||
          processedUpdates.current.has(zoomContext.lastUpdated)) {
        return;
      }
      
      // Mark this update as processed
      processedUpdates.current.add(zoomContext.lastUpdated);
      
      // Store current timeRange values
      const start = zoomContext.timeRange.start;
      const end = zoomContext.timeRange.end;
      
      // Add a small delay to avoid excessive fetching during rapid interactions
      const timeoutId = setTimeout(() => {
        // Set both local and shared loading states
        setLoading(true);
        if (zoomContext) {
          zoomContext.setIsLoading(true);
        }
        
        fetchPlotData(props.sessionId, undefined, start, end, props.sensorId, undefined)
          .then((result) => {
            if (result && result.status === 200 && result.data) {
              setPlotData((prevData: any[]) => {
                // Merge new data with existing data
                const mergedData = [...(prevData || [])];
                result.data.forEach((item: any) => {
                  const index = mergedData.findIndex((e: any) => e.timestamp === item.timestamp);
                  if (index === -1) {
                    mergedData.push(item);
                  } else {
                    mergedData[index] = { ...mergedData[index], ...item };
                  }
                });
                
                // Sort by timestamp
                mergedData.sort((a: any, b: any) => a.timestamp.localeCompare(b.timestamp));
                
                // Find the new indexes in the merged data for the current time range
                if (zoomContext && zoomContext.timeRange.start && zoomContext.timeRange.end) {
                  // Find the exact indexes for the current time range in the merged data
                  const startIdx = mergedData.findIndex((e: any) => e.timestamp === start);
                  const endIdx = mergedData.findIndex((e: any) => e.timestamp === end);
                  
                  // If we found valid indexes, update them in the context
                  if (startIdx !== -1) zoomContext.setStartIndex(startIdx);
                  if (endIdx !== -1) zoomContext.setEndIndex(endIdx);
                  
                  // Signal that new data is ready to be displayed
                  zoomContext.setDataReady(true);
                  
                  console.log(`GraphsContainer: Updated zoom indexes to ${startIdx}-${endIdx} for time range ${start}-${end}`);
                }
                
                return mergedData;
              });
            }
          })
          .finally(() => {
            // Clear both local and shared loading states
            setLoading(false);
            if (zoomContext) {
              zoomContext.setIsLoading(false);
            }
          });
      }, 250); // Slight debounce to handle rapid changes
      
      return () => clearTimeout(timeoutId);
    }, [zoomContext, props.sessionId]);
    
    return content;
  };

  return (
    <Container fluid className="px-0 pt-2">
      <ZoomProvider>
        <Row className="pb-2">
          <Col>
            <h1>Graphs</h1>
          </Col>
          <Col className="text-end">
            <DropdownButton
              icon={<BsFilter />}
              text={"Filters"}
              dropdownContent={filterContent}
              endAlign={true}
            />
          </Col>
        </Row>
        <GraphsWithZoomContext />
      </ZoomProvider>
    </Container>
  );
}

function createGraphsArray() {
  return [
    {
      id: "speed_combined",
      name: "Trajectory Speed & Apparent Wind [m/s]",
      xKey: "timestamp",
      yKey: ["trajectorySpeed_ms", "apparentWindSpeedMagnitude_ms"],
      default: true,
    },
    {
      id: "apparentWindAngles_combined",
      name: "Apparent Wind Pitch & Yaw [°]",
      xKey: "timestamp",
      yKey: ["apparentWindYawAngleOfAttack_rad", "apparentWindPitchAngleOfAttack_rad"],
      default: true,
    },
    {
      id: "trajectorySpeed",
      name: "Trajectory Speed [m/s]",
      xKey: "timestamp",
      yKey: "trajectorySpeed_ms",
      default: false,
    },
    {
      id: "apparentWindSpeed",
      name: "Apparent Wind Speed [m/s]",
      xKey: "timestamp",
      yKey: "apparentWindSpeedMagnitude_ms",
      default: false,
    },
    {
      id: "apparentWindPitchAngleOfAttack",
      name: "Apparent Wind Pitch Angle of Attack [°]",
      xKey: "timestamp",
      yKey: "apparentWindPitchAngleOfAttack_rad",
      default: false,
    },
    {
      id: "apparentWindYawAngleOfAttack",
      name: "Apparent Wind Yaw Angle of Attack [°]",
      xKey: "timestamp",
      yKey: "apparentWindYawAngleOfAttack_rad",
      default: false,
    },
    // {
    //   id: "ambientWindSpeed",
    //   name: "Ambient Wind Speed",
    //   xKey: "timestamp",
    //   yKey: "ambientWindSpeedMagnitude_ms",
    //   default: false,
    // },
    // {
    //   id: "ambientWindPitchAngleOfAttack",
    //   name: "Ambient Wind Pitch Angle of Attack [°]",
    //   xKey: "timestamp",
    //   yKey: "ambientWindPitchAngleOfAttack_rad",
    //   default: false,
    // },
    // {
    //   id: "ambientWindYawAngleOfAttack",
    //   name: "Ambient Wind Yaw Angle of Attack [°]",
    //   xKey: "timestamp",
    //   yKey: "ambientWindYawAngleOfAttack_rad",
    //   default: false,
    // },
    {
      id: "temp",
      name: "Temperature [°C]",
      xKey: "timestamp",
      yKey: "temp",
      default: false,
    },
    {
      id: "hum",
      name: "Humidity [%]",
      xKey: "timestamp",
      yKey: "hum",
      default: false,
    },
    {
      id: "accel",
      name: "Accelerometer [ms²]",
      xKey: "timestamp",
      yKey: ["accel_x", "accel_y", "accel_z"],
      default: false,
    },
    {
      id: "gyro",
      name: "Gyroscope",
      xKey: "timestamp",
      yKey: ["gyro_x", "gyro_y", "gyro_z"],
      default: false,
    },
    {
      id: "angle",
      name: "Angle",
      xKey: "timestamp",
      yKey: ["angle_x", "angle_y", "angle_z", "angle_w"],
      default: false,
    },
    // {
    //   id: "position",
    //   name: "Position",
    //   xKey: "timestamp",
    //   yKey: ["lat", "lon", "alt"],
    //   default: false,
    // },
    // {
    //   id: "Tu",
    //   name: "Turbulence Level",
    //   xKey: "timestamp",
    //   yKey: "Tu",
    //   default: false,
    // },
    // {
    //   id: "unev",
    //   name: "Unevenness Score",
    //   xKey: "timestamp",
    //   yKey: "unev",
    //   default: false,
    // },
    // {
    //   id: "gyro_stab",
    //   name: "Stability Score",
    //   xKey: "timestamp",
    //   yKey: "gyro_stab",
    //   default: false,
    // },
    // { id: "chi", name: "Unknown metric", xKey: "timestamp", yKey: "chi" },
  ];
}
